// Dòng chảy khách hàng giữa các bộ phận: các điểm bàn giao (Trực page → Telesale → Sale cơ sở → hoàn tất)
// và dải giai đoạn S0 → S8 ghép từ thẻ khách của Trực page, Telesale và Sale cơ sở.
import { ratio, totals, type Dept, type Snapshot } from './insights';

const n = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** Kết quả "Chưa gọi" của Trực page (API trả id = null, tên "Chưa gọi") */
export function uncalledCount(s: Snapshot): number | null {
  const calls = s.page?.call_result_summary;
  if (!calls?.length) return null;
  const row = calls.find(x => /chưa gọi/i.test(x.name)) ?? calls.find(x => x.id === null || x.id === 0);
  return row ? n(row.count) : 0;
}

/** Lấy số thẻ khách (customer_tag_id) từ chuỗi kiểu "Tag 12" / "Chốt & Hoàn thành dịch vụ (Tag 19)" */
export const tagIdOf = (s?: string | null) => {
  const m = String(s || '').match(/tag\s*(\d+)/i);
  return m ? Number(m[1]) : null;
};

/** Số thứ tự giai đoạn từ "S6.1 - ..." → 6.1 (chấp nhận cả cách viết "S.8 - Hoàn Thành") */
export const stageNo = (name: string) => {
  const m = String(name).match(/\bS\s*\.?\s*(\d+(?:\.\d+)?)/i);
  return m ? Number(m[1]) : null;
};

export type Health = 'good' | 'warn' | 'bad' | 'none';
const health = (v: number | null, good: number, warn: number): Health => (v === null ? 'none' : v >= good ? 'good' : v >= warn ? 'warn' : 'bad');

export interface Handoff {
  key: string;
  from: Dept;
  to: Dept | 'done';
  title: string;
  /** Số khách được giao sang */
  input: number;
  inputLabel: string;
  /** Số khách bên nhận đã xử lý */
  output: number;
  outputLabel: string;
  rate: number | null;
  prevRate: number | null;
  /** Khách còn tồn ở điểm bàn giao */
  backlog: number;
  backlogLabel: string;
  health: Health;
  note?: string;
  /** Mở danh sách khách đang tồn */
  tagId?: number | null;
}

/** Ngưỡng đánh giá điểm bàn giao (%) */
export const HANDOFF_RULES = { calledGood: 80, calledWarn: 50, showGood: 60, showWarn: 40, closeGood: 50, closeWarn: 30, mismatch: 0.2 };

export function handoffs(cur: Snapshot, prev: Snapshot): Handoff[] {
  const c = totals(cur);
  const p = totals(prev);
  const out: Handoff[] = [];

  // 1. Trực page → Telesale: khách mới đã được gọi chưa
  const unc = uncalledCount(cur);
  const pUnc = uncalledCount(prev);
  const callBase = n(cur.page?.call_result_summary?.reduce((s, x) => s + n(x.count), 0));
  const pCallBase = n(prev.page?.call_result_summary?.reduce((s, x) => s + n(x.count), 0));
  const called = unc === null ? 0 : Math.max(0, callBase - unc);
  const s0 = cur.page?.customer_by_tags?.find(t => stageNo(t.name) === 0);
  out.push({
    key: 'page-tele', from: 'page', to: 'tele', title: 'Trực page → Telesale',
    input: callBase || c.leads, inputLabel: 'khách cần gọi',
    output: called, outputLabel: 'đã được gọi',
    rate: unc === null ? null : ratio(called, callBase),
    prevRate: pUnc === null ? null : ratio(Math.max(0, pCallBase - pUnc), pCallBase),
    backlog: unc ?? 0, backlogLabel: 'chưa gọi',
    health: health(unc === null ? null : ratio(called, callBase), HANDOFF_RULES.calledGood, HANDOFF_RULES.calledWarn),
    tagId: s0?.id ?? null,
  });

  // 2. Telesale → Sale cơ sở: lịch đặt có thành khách đến không
  const sameSource = c.teleShowup && c.showup && Math.abs(c.teleShowup - c.showup) / Math.max(c.teleShowup, c.showup) > HANDOFF_RULES.mismatch;
  out.push({
    key: 'tele-sale', from: 'tele', to: 'sale', title: 'Telesale → Sale cơ sở',
    input: c.booked, inputLabel: 'lịch hẹn đã đặt',
    output: c.teleShowup, outputLabel: 'khách đã đến',
    rate: c.showupRate, prevRate: p.showupRate,
    backlog: Math.max(0, c.booked - c.teleShowup), backlogLabel: 'chưa đến / vắng hẹn',
    health: health(c.showupRate, HANDOFF_RULES.showGood, HANDOFF_RULES.showWarn),
    note: sameSource ? `Telesale ghi nhận ${c.teleShowup} khách đến, Sale cơ sở ghi nhận ${c.showup} — cần đối soát.` : undefined,
    tagId: tagIdOf(cur.tele?.tag_summary?.find(t => stageNo(t.tag) === 5)?.code),
  });

  // 3. Sale cơ sở → hoàn tất: khách đến có chốt / hoàn thành không
  const statuses = cur.sale?.status_summary || [];
  const waiting = statuses.find(x => /chờ|đợi/i.test(`${x.name} ${x.tag ?? ''}`));
  const done = statuses.find(x => /hoàn thành/i.test(x.name));
  out.push({
    key: 'sale-done', from: 'sale', to: 'done', title: 'Sale cơ sở → Chốt dịch vụ',
    input: c.showup, inputLabel: 'khách đến cơ sở',
    output: c.closed, outputLabel: 'đã chốt',
    rate: c.closeRate, prevRate: p.closeRate,
    backlog: waiting ? n(waiting.total) : Math.max(0, c.showup - c.closed), backlogLabel: waiting ? 'đang chờ tư vấn / làm' : 'chưa chốt',
    health: health(c.closeRate, HANDOFF_RULES.closeGood, HANDOFF_RULES.closeWarn),
    note: done ? `${n(done.total)} khách đã hoàn thành dịch vụ & ra về.` : undefined,
    tagId: tagIdOf(waiting?.name),
  });
  // chưa có khách nào được giao sang (ví dụ đầu ngày) thì chưa đánh giá, tránh báo "tắc nghẽn" sai
  return out.map(h => (h.input > 0 ? h : { ...h, rate: null, health: 'none' as Health }));
}

export interface Stage { key: string; no: number; label: string; count: number; owner: Dept; tagId: number | null; desc?: string }

const DONE_NO = 9;

/** Dải giai đoạn S0 → S8: S0–S4 của Trực page, S5–S6.x của Telesale, S7 trở đi của Sale cơ sở */
export function lifecycleStages(s: Snapshot): Stage[] {
  const map = new Map<string, Stage>();
  const ownerOf = (no: number): Dept => (no < 5 ? 'page' : no < 7 ? 'tele' : 'sale');
  // "S.8 - Hoàn Thành" cùng số 8 với "S8.0 - Upsale…" → tách riêng và xếp cuối dải, không bị gộp mất.
  // Chỉ xét thẻ số 8: thẻ giai đoạn khác có chữ "hoàn thành" (vd. S6.x) vẫn giữ đúng chỗ và số khách của nó
  const put = (st: Stage) => {
    const done = st.no === 8 && /hoàn thành/i.test(st.label);
    const id = done ? 'done' : String(st.no);
    if (!map.has(id)) map.set(id, done ? { ...st, no: DONE_NO } : st);
  };
  const pageTags = s.page?.customer_by_tags || [];
  const putPage = (keep: (no: number) => boolean) => pageTags.forEach(t => {
    const no = stageNo(t.name);
    if (no !== null && keep(no)) put({ key: `p${t.id}`, no, label: t.name, count: n(t.count), owner: ownerOf(no), tagId: t.id, desc: t.description || undefined });
  });
  // S0–S4 lấy từ Trực page, S5 trở đi ưu tiên Telesale (P_v2 cũng trả các thẻ này — chỉ dùng khi Telesale thiếu)
  putPage(no => no < 5);
  (s.tele?.tag_summary || []).forEach(t => {
    const no = stageNo(t.tag);
    if (no !== null) put({ key: `t${t.code}`, no, label: t.tag, count: n(t.total), owner: ownerOf(no), tagId: tagIdOf(t.code), desc: t.desc });
  });
  putPage(no => no >= 5);
  return Array.from(map.values()).sort((a, b) => a.no - b.no);
}
